import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
} from '@mui/material';
import { TableChart, Send, Close, Download } from '@mui/icons-material';
import axios from 'axios';

interface RemedialEntry {
  id: number;
  project_id: number;
  repair: string;
  description: string | null;
  location_of_materials: string | null;
  customer_contact_notes: string | null;
  date_invoice_paid: string | null;
  date_materials_invoice_paid: string | null;
  whos_at_fault: string | null;
  status: 'open' | 'resolved';
  material_cost: string | number | null;
  start_date: string | null;
  resolved_date: string | null;
  project_title: string;
  order_number: string | null;
  customer_name: string;
  customer_address: string;
  phone: string | null;
}

function formatDate(val: string | null) {
  if (!val) return '';
  return val.split('T')[0];
}

function formatCurrency(val: string | number | null) {
  if (val === null || val === undefined || val === '') return '';
  const num = parseFloat(String(val));
  if (isNaN(num) || num === 0) return '';
  return `£${num.toFixed(2)}`;
}

const thStyle: React.CSSProperties = {
  padding: '6px 4px',
  textAlign: 'center',
  fontWeight: 'bold',
  fontSize: '11px',
  color: '#333',
  whiteSpace: 'nowrap',
  borderBottom: '2px solid #bdc3c7',
  borderRight: '1px solid #bdc3c7',
};

const tdStyle: React.CSSProperties = {
  padding: '4px 6px',
  borderBottom: '1px solid #e8e8e8',
  borderRight: '1px solid #d0d0d0',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  verticalAlign: 'top',
  fontSize: '12px',
};

const tdNarrow: React.CSSProperties = {
  ...tdStyle,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export default function RemedialsDiary() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tab, setTab] = useState(0);
  const [remedials, setRemedials] = useState<RemedialEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Email modal state
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to: 'l.harper43@ntlworld.com',
    cc: 'gordonsmakeovers@hotmail.com; info@gordonsmakeovers.co.uk',
    subject: `Remedials Diary - ${new Date().toLocaleDateString('en-GB')}`,
    message: 'Please find attached the current Remedials Diary.\n\nThanks,\nGordons Makeovers',
  });
  const [sending, setSending] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const excelFilename = `Remedials_Diary_${today}.xlsx`;

  const handleOpenEmailModal = () => {
    setEmailForm({
      to: 'l.harper43@ntlworld.com',
      cc: 'gordonsmakeovers@hotmail.com; info@gordonsmakeovers.co.uk',
      subject: `Remedials Diary - ${new Date().toLocaleDateString('en-GB')}`,
      message: 'Please find attached the current Remedials Diary.\n\nThanks,\nGordons Makeovers',
    });
    setEmailError(null);
    setEmailSuccess(false);
    setEmailOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.to.trim()) { setEmailError('Please enter a recipient email address.'); return; }
    if (!emailForm.subject.trim()) { setEmailError('Please enter a subject.'); return; }
    setSending(true);
    setEmailError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/remedials-diary/send-excel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(emailForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send');
      setEmailSuccess(true);
      setTimeout(() => setEmailOpen(false), 2000);
    } catch (err: any) {
      setEmailError(err.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    axios.get('/api/remedials-diary')
      .then(res => setRemedials(res.data))
      .catch(err => console.error('Error loading remedials diary:', err))
      .finally(() => setLoading(false));
  }, []);

  const current = remedials.filter(r => r.status === 'open');
  const completed = remedials.filter(r => r.status === 'resolved');
  const rows = tab === 0 ? current : completed;

  const isCompleted = tab === 1;

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Title */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: { xs: '1.1rem', md: '1.5rem' } }}>
          Remedials Diary
        </Typography>
        {tab === 0 && (
          <Button
            variant="contained"
            size="small"
            startIcon={<TableChart />}
            onClick={handleOpenEmailModal}
            sx={{ backgroundColor: '#27ae60', '&:hover': { backgroundColor: '#219150' } }}
          >
            Export &amp; Email Diary
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 2, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          TabIndicatorProps={{ sx: { height: 3 } }}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: { xs: '0.75rem', md: '0.9rem' },
              minHeight: 40,
              px: { xs: 1, md: 2 },
              py: 1,
            },
          }}
        >
          <Tab label={`Current Remedials (${current.length})`} />
          <Tab label={`Completed Remedials (${completed.length})`} />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {/* Section header — matches fitters diary dark header */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#2c3e50',
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: '4px 4px 0 0',
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
              {isCompleted ? 'COMPLETED REMEDIALS' : 'CURRENT REMEDIALS'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
            </Typography>
          </Box>

          {/* Table */}
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontFamily: '"Roboto Mono", "Courier New", monospace',
              fontSize: isMobile ? '11px' : '13px',
              minWidth: isCompleted ? '1500px' : '1200px',
              borderLeft: '1px solid #d0d0d0',
            }}>
              <thead>
                <tr style={{ backgroundColor: '#ecf0f1', borderBottom: '2px solid #bdc3c7' }}>
                  <th style={{ ...thStyle, width: '100px', textAlign: 'left' }}>NAME</th>
                  <th style={{ ...thStyle, width: '160px', textAlign: 'left' }}>ADDRESS</th>
                  <th style={{ ...thStyle, width: '200px', textAlign: 'left' }}>REPAIR</th>
                  <th style={{ ...thStyle, width: '110px' }}>NUMBER</th>
                  <th style={{ ...thStyle, width: '70px' }}>REF</th>
                  <th style={{ ...thStyle, width: '160px', textAlign: 'left' }}>FURTHER INFO</th>
                  <th style={{ ...thStyle, width: '85px' }}>FIT DATE</th>
                  <th style={{ ...thStyle, width: '170px', textAlign: 'left' }}>LOCATION OF MATERIALS</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>CONTACT MADE / CONFIRMED WITH CUSTOMER / NOTES FOR ALAN</th>
                  {isCompleted && <>
                    <th style={{ ...thStyle, width: '100px' }}>FITTER INVOICE PAID</th>
                    <th style={{ ...thStyle, width: '100px' }}>MATERIALS INVOICE PAID</th>
                    <th style={{ ...thStyle, width: '90px' }}>MATERIALS COST</th>
                    <th style={{ ...thStyle, width: '100px' }}>WHO'S AT FAULT?</th>
                  </>}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={isCompleted ? 13 : 9}
                      style={{ ...tdStyle, textAlign: 'center', padding: '24px', color: '#999', fontStyle: 'italic' }}
                    >
                      No {isCompleted ? 'completed' : 'current'} remedials found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => {
                    const incomplete = !r.repair;
                    const rowBg = incomplete
                      ? '#fff8e1'
                      : idx % 2 === 0 ? 'white' : '#f9f9f9';
                    return (
                      <tr
                        key={`${r.project_id}-${r.id ?? idx}`}
                        style={{ backgroundColor: rowBg, borderBottom: '1px solid #e0e0e0' }}
                      >
                        <td style={{ ...tdNarrow, fontWeight: 'bold', color: '#2c3e50', textTransform: 'uppercase' }}>
                          {r.customer_name || '—'}
                        </td>
                        <td style={{ ...tdStyle, fontSize: '11px' }}>{r.customer_address || ''}</td>
                        <td style={{ ...tdStyle, fontSize: isMobile ? '10px' : '12px', fontStyle: incomplete ? 'italic' : 'normal', color: incomplete ? '#bbb' : 'inherit' }}>
                          {incomplete ? 'Not filled in' : r.repair}
                        </td>
                        <td style={{ ...tdNarrow, textAlign: 'center' }}>{r.phone || ''}</td>
                        <td style={{ ...tdNarrow, textAlign: 'center', fontWeight: 'bold' }}>{r.order_number || ''}</td>
                        <td style={{ ...tdStyle, fontSize: '11px' }}>{r.description || ''}</td>
                        <td style={{ ...tdNarrow, textAlign: 'center' }}>{formatDate(r.start_date)}</td>
                        <td style={{ ...tdStyle, fontSize: '11px' }}>{r.location_of_materials || ''}</td>
                        <td style={{ ...tdStyle, fontSize: '11px', color: '#444' }}>{r.customer_contact_notes || ''}</td>
                        {isCompleted && <>
                          <td style={{ ...tdNarrow, textAlign: 'center' }}>{formatDate(r.date_invoice_paid)}</td>
                          <td style={{ ...tdNarrow, textAlign: 'center' }}>{formatDate(r.date_materials_invoice_paid)}</td>
                          <td style={{ ...tdNarrow, textAlign: 'center', fontWeight: 'bold' }}>{formatCurrency(r.material_cost)}</td>
                          <td style={{ ...tdNarrow, textAlign: 'center' }}>{r.whos_at_fault || ''}</td>
                        </>}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Box>
        </Box>
      )}

      {/* Email Modal */}
      <Dialog open={emailOpen} onClose={() => !sending && setEmailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TableChart color="success" />
            <Typography variant="h6">Email Remedials Diary</Typography>
          </Box>
          <IconButton size="small" onClick={() => setEmailOpen(false)} disabled={sending}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {emailSuccess ? (
            <Alert severity="success">Email sent successfully!</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Attachment info */}
              <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableChart fontSize="small" color="action" />
                  <Typography variant="body2" fontWeight="medium">{excelFilename}</Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<Download />}
                  sx={{ fontSize: '0.75rem' }}
                  onClick={async () => {
                    const token = localStorage.getItem('token');
                    const res = await fetch('/api/remedials-diary/export-excel', {
                      headers: { 'Authorization': `Bearer ${token}` },
                    });
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = excelFilename;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Preview
                </Button>
              </Box>

              <TextField
                label="To"
                value={emailForm.to}
                onChange={e => setEmailForm({ ...emailForm, to: e.target.value })}
                fullWidth
                required
                disabled={sending}
                helperText="Separate multiple emails with semicolon (;)"
              />
              <TextField
                label="CC"
                value={emailForm.cc}
                onChange={e => setEmailForm({ ...emailForm, cc: e.target.value })}
                fullWidth
                disabled={sending}
                helperText="Separate multiple emails with semicolon (;)"
              />
              <TextField
                label="Subject"
                value={emailForm.subject}
                onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })}
                fullWidth
                required
                disabled={sending}
              />
              <TextField
                label="Message"
                value={emailForm.message}
                onChange={e => setEmailForm({ ...emailForm, message: e.target.value })}
                fullWidth
                multiline
                rows={4}
                disabled={sending}
              />
              {emailError && <Alert severity="error" onClose={() => setEmailError(null)}>{emailError}</Alert>}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEmailOpen(false)} disabled={sending} variant="outlined">Cancel</Button>
          <Button
            onClick={handleSendEmail}
            disabled={sending || emailSuccess}
            variant="contained"
            startIcon={sending ? <CircularProgress size={18} /> : <Send />}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
